/**
 * What a tenant needs from its operator, as rows in severity order. Pure:
 * the page gathers the reads, this decides what they mean. `broken` is
 * costing someone something now; `waiting` is a decision or a step that is
 * due.
 */
import type { RecordRef } from '../../components/shell/record.js';

export type NeedSeverity = 'broken' | 'waiting';

export type NeedAction =
  | { kind: 'navigate'; label: string; to: string }
  | { kind: 'record'; label: string; ref: RecordRef }
  | { kind: 'scroll'; label: string; target: string };

export interface TenantNeed {
  id: string;
  severity: NeedSeverity;
  icon: string;
  title: string;
  detail: string;
  action: NeedAction;
}

export interface TenantNeedsInput {
  orgId: string;
  /** Failed tasks inside the window the page reads (24 hours). */
  failedTasks: number;
  /** Paths whose status is `needs_migration`, in table order. */
  migrationPaths: string[];
  /** Groups that grant paths but have no member who could reach them. */
  unreachableGroups: { id: string; name: string; collections: number }[];
  /** Null until the onboarding steps are known, or when every step is done. */
  onboarding: { done: number; total: number } | null;
}

const RANK: Record<NeedSeverity, number> = { broken: 0, waiting: 1 };

function plural(n: number, one: string, many = `${one}s`): string {
  return `${n} ${n === 1 ? one : many}`;
}

export function buildTenantNeeds(input: TenantNeedsInput): TenantNeed[] {
  const base = `/orgs/${input.orgId}`;
  const rows: TenantNeed[] = [];

  if (input.failedTasks > 0) {
    rows.push({
      id: 'failed-tasks',
      severity: 'broken',
      icon: 'error',
      title: `${plural(input.failedTasks, 'task')} failed in the last 24 hours`,
      detail: 'A failed task answered nobody. The list says which capability and why.',
      action: { kind: 'navigate', label: 'Open failures', to: `${base}/tasks?tab=failures` },
    });
  }

  for (const group of input.unreachableGroups) {
    rows.push({
      id: `group:${group.id}`,
      severity: 'broken',
      icon: 'shield_person',
      title: `Group ${group.name} reaches nothing`,
      detail: `${plural(group.collections, 'path')} granted, no member who can ask. Those documents can be retrieved by no one.`,
      action: {
        kind: 'record',
        label: 'Open group',
        ref: { kind: 'group', id: group.id, orgId: input.orgId },
      },
    });
  }

  if (input.migrationPaths.length > 0) {
    const first = input.migrationPaths[0] ?? '';
    rows.push({
      id: 'migration',
      severity: 'waiting',
      icon: 'drive_file_move',
      title: `${plural(input.migrationPaths.length, 'path')} need${input.migrationPaths.length === 1 ? 's' : ''} migration`,
      detail: input.migrationPaths.slice(0, 3).join(' · '),
      action: {
        kind: 'record',
        label: 'Open to migrate',
        ref: { kind: 'collection', id: first, orgId: input.orgId },
      },
    });
  }

  if (input.onboarding && input.onboarding.done < input.onboarding.total) {
    rows.push({
      id: 'onboarding',
      severity: 'waiting',
      icon: 'checklist',
      title: `Onboarding ${input.onboarding.done} of ${input.onboarding.total}`,
      detail: 'The tenant is not set up end to end yet. The checklist says what is left.',
      action: { kind: 'scroll', label: 'Show checklist', target: 'onboarding' },
    });
  }

  return rows.sort((a, b) => RANK[a.severity] - RANK[b.severity]);
}
